import React, { useState } from "react";
import { API_BASE_URL } from "../constants";
import axiosInstance from "../interceptors/token";
import Header from "./Header";
import { Trans } from "@lingui/react/macro";
import { i18n } from '@lingui/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpState, setOtpState] = useState(false); // State for OTP verification mode
    const buttonText = loading 
    ? (otpState ? i18n._("Verifying...") : i18n._("Logging in...")) 
    : (otpState ? i18n._("Verify OTP") : i18n._("Login"));

    const getDeviceInfo = async () => {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        return result.visitorId;
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const device = await getDeviceInfo();
            const response = await axiosInstance.post(`${API_BASE_URL}token/`, {
                username,
                password,
                device,
            });

            if (response.status === 200) {
                localStorage.setItem("access_token", response.data.access);
                localStorage.setItem("refresh_token", response.data.refresh);
                localStorage.setItem("username", username);

                window.location.href = "/projects";
            } else if (response.status === 202) {
                setOtpState(true); 
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError(<Trans>Invalid username or password</Trans>);
            } else if (err.response?.status === 400) {
                setError(<Trans>Your email address has not been set on the API. Contact Bartel to resolve this issue.</Trans>);
            } else {
                setError(<Trans>An unexpected error occurred</Trans>);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const device = await getDeviceInfo();
            const response = await axiosInstance.post(`${API_BASE_URL}otp/`, {
                username,
                otp,
                device,
            });

            if (response.status === 200) {
                localStorage.setItem("access_token", response.data.access);
                localStorage.setItem("refresh_token", response.data.refresh);
                localStorage.setItem("username", username);

                window.location.href = "/projects";
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError(<Trans>Username not found</Trans>);
            } else if (err.response?.status === 400) {
                setError(<Trans>Invalid OTP</Trans>);
            } else {
                setError(<Trans>An unexpected error occurred</Trans>);
            }
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <div style={styles.container}>
                <div style={styles.card}>
                    <img src="/logo_full.jpg" alt="Logo" style={styles.logo} />
                    {!otpState ? (
                        <form onSubmit={handleLoginSubmit} style={styles.form}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}><Trans>Username</Trans>:</label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    style={styles.input}
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}><Trans>Password</Trans>:</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={styles.input}
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <button
                                type="submit"
                                style={{
                                    ...styles.button,
                                    backgroundColor: loading ? "#6c757d" : "#007BFF",
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                                disabled={loading}
                            >
                                {loading 
                                    ? (otpState 
                                        ? <Trans>Verifying...</Trans> 
                                        : <Trans>Logging in...</Trans>)
                                    : (otpState 
                                        ? <Trans>Verify OTP</Trans> 
                                        : <Trans>Login</Trans>)
                                }

                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleOtpSubmit} style={styles.form}>
                            <p style={styles.info}>
                                <Trans>You have been sent an email with an OTP code.</Trans><br></br><Trans>Please check your email and enter the code below.</Trans>
                            </p>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>OTP:</label>
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    style={styles.input}
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <button
                                type="submit"
                                style={{
                                    ...styles.button,
                                    backgroundColor: loading ? "#6c757d" : "#007BFF",
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                                disabled={loading}
                            >
                                {buttonText}
                            </button>
                        </form>
                    )}
                    {error && <p style={styles.error}>{error}</p>}
                </div>
            </div>
        </div>
    );
};

// Inline styles for the Login component
const styles = {
    container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f4f4f9",
    },
    card: {
        width: "400px",
        padding: "30px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#fff",
        textAlign: "center",
    },
    logo: {
        width: "150px",
        marginBottom: "20px",
    },
    form: {
        display: "flex",
        flexDirection: "column",
    },
    inputGroup: {
        marginBottom: "15px",
    },
    label: {
        display: "block",
        marginBottom: "5px",
        fontWeight: "bold",
        color: "#555",
    },
    input: {
        width: "100%",
        padding: "10px",
        fontSize: "16px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        outline: "none",
        boxSizing: "border-box",
    },
    button: {
        width: "100%",
        padding: "10px",
        fontSize: "16px",
        fontWeight: "bold",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        transition: "background-color 0.3s, opacity 0.3s",
    },
    error: {
        marginTop: "10px",
        color: "red",
        fontSize: "14px",
        textAlign: "center",
    },
};

export default Login;
